import {
  createApiFactory,
  createApiRef,
  discoveryApiRef,
  oauthRequestApiRef,
  configApiRef,
  createFrontendModule,
  ApiBlueprint,
} from '@backstage/frontend-plugin-api';
import { SignInPageBlueprint } from '@backstage/plugin-app-react';
import { OAuth2 } from '@backstage/core-app-api';
import { SignInPage } from '@backstage/core-components';
import type {
  OAuthApi,
  OpenIdConnectApi,
  ProfileInfoApi,
  BackstageIdentityApi,
  SessionApi,
} from '@backstage/core-plugin-api';

/**
 * API ref for the Zitadel OAuth provider.
 * Must match the providerId registered in the backend ('zitadel').
 */
export const zitadelAuthApiRef = createApiRef<
  OAuthApi & OpenIdConnectApi & ProfileInfoApi & BackstageIdentityApi & SessionApi
>({
  id: 'internal.auth.zitadel',
});

/**
 * Frontend module that adds:
 * 1. A Zitadel OAuth2 API factory
 * 2. A sign-in page with Zitadel + Guest options
 */
export const signInModule = createFrontendModule({
  pluginId: 'app',
  extensions: [
    // Register the Zitadel OAuth2 API via ApiBlueprint
    ApiBlueprint.make({
      name: 'zitadel-auth',
      params: defineParams => defineParams(
        createApiFactory({
          api: zitadelAuthApiRef,
          deps: {
            discoveryApi: discoveryApiRef,
            oauthRequestApi: oauthRequestApiRef,
            configApi: configApiRef,
          },
          factory: ({ discoveryApi, oauthRequestApi, configApi }) =>
            OAuth2.create({
              discoveryApi,
              oauthRequestApi,
              provider: {
                id: 'zitadel',
                title: 'Zitadel',
                icon: () => null,
              },
              defaultScopes: [
                'openid',
                'profile',
                'email',
                'urn:zitadel:iam:org:project:roles',
              ],
              environment: configApi.getOptionalString('auth.environment'),
            }),
        }),
      ),
    }),

    // Custom sign-in page showing both providers
    SignInPageBlueprint.make({
      params: {
        loader: async () => props => (
          <SignInPage
            {...props}
            providers={[
              {
                id: 'zitadel',
                title: 'Sign in with Zitadel',
                message: 'Sign in to MAC Developer Portal using your organization account',
                apiRef: zitadelAuthApiRef,
              },
              'guest',
            ]}
          />
        ),
      },
    }),
  ],
});
